import React, { useEffect, useMemo, useRef, useState } from "react";
import BillReceipt from "./BillReciept";

const FAST = ["Gulab Jamun", "Rasgulla", "Samosa", "Lassi"];

const STORE = {
  name: "Jaggi Sweets",
  tagline: "Official Billing & POS System",
  upi: "jaggisweets@upi",
  footer: [
    "Thank you for shopping at Jaggi Sweets!",
    "Visit Again!",
    "Customer Care: +91 9XXXXXXXXX",
    "GSTIN: XXAAAA0000X",
    "Software by Jaggi Tech",
  ],
};

const genInvoiceId = () =>
  "INV-" + Math.random().toString(36).substring(2, 8).toUpperCase();

const genBillNo = () => {
  const y = new Date().getFullYear();
  const n = Math.floor(Math.random() * 90000 + 10000);
  return `JS-${y}-${n}`;
};

const isWeightItem = (item) => item.uom === "KG";

const lineAmount = (item) => {
  const rate = Number(item.overriddenPrice ?? item.price ?? 0);

  return isWeightItem(item)
    ? (Number(item.grams || 0) / 1000) * rate
    : Number(item.qty || 0) * rate;
};

const makePayloadItems = (items) =>
  items.map((item) => ({
    id: item.id,
    name: item.name,
    qty: isWeightItem(item)
      ? Number(item.grams || 0) / 1000
      : Number(item.qty || 0),
    grams: isWeightItem(item) ? Number(item.grams || 0) : null,
    price: Number(item.overriddenPrice ?? item.price),
  }));

export default function BillingPOS_Enterprise() {
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [bill, setBill] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [paid, setPaid] = useState(0);
  const [mode, setMode] = useState("Cash");
  const [selectedRow, setSelectedRow] = useState(null);
  const [keypadTarget, setKeypadTarget] = useState("qty");
  const [holds, setHolds] = useState([]);
  const [holdId, setHoldId] = useState(null);
  const [billNo, setBillNo] = useState(genBillNo());
  const [invoiceId, setInvoiceId] = useState(genInvoiceId());
  const [refundMode, setRefundMode] = useState(false);
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarks, setRemarks] = useState("");
  const barcodeBuffer = useRef("");
  const barcodeTimer = useRef(null);
  const [keypadMode, setKeypadMode] = useState("qty");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showOpening, setShowOpening] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [openingRemarks, setOpeningRemarks] = useState("");
  const [showDayClosing, setShowDayClosing] = useState(false);
  const [closingSummary, setClosingSummary] = useState(null);
  const [closingActualCash, setClosingActualCash] = useState("");
  const [closingExpenses, setClosingExpenses] = useState(0);
  const [closingLoading, setClosingLoading] = useState(false);
  const [tokenNo, setTokenNo] = useState("");
  const [showVoid, setShowVoid] = useState(false);
  const [voidBillId, setVoidBillId] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [voidLoading, setVoidLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [billSearch, setBillSearch] = useState("");
  const [tokenSearch, setTokenSearch] = useState("");
  const [billResults, setBillResults] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [lastPrintedBill, setLastPrintedBill] = useState(null);
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundBillId, setRefundBillId] = useState(null);
  const [returnToInventory, setReturnToInventory] = useState(true);
  const [showExpense, setShowExpense] = useState(false);
  const [expenseType, setExpenseType] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseRemarks, setExpenseRemarks] = useState("");
  const [expenseLoading, setExpenseLoading] = useState(false);

  useEffect(() => {
    checkAuth();
    loadMenu();
    checkDayOpening();
    loadHolds();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch("/api/billing/me", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status !== "success") {
        window.location.href = "/billinglogin";
        return;
      }

      setUser(data.user);
    } catch {
      window.location.href = "/billinglogin";
    }
  };

  const loadHolds = async () => {
    try {
      const res = await fetch("/api/pos/holds", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setHolds(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadDayClosing = async () => {
    try {
      const res = await fetch("/api/pos/day-closing-summary", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      setClosingSummary(data.data);
      setClosingActualCash(data.data.expected_cash);
      setClosingExpenses(0);
      setShowDayClosing(true);
    } catch (err) {
      console.error(err);
    }
  };

  const voidBill = async () => {
    if (!voidReason.trim()) {
      alert("Please enter a reason.");
      return;
    }

    setVoidLoading(true);

    try {
      const res = await fetch("/api/pos/void-bill", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bill_id: voidBillId,
          reason: voidReason,
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        console.log(data.message);
        return;
      }

      alert("Bill voided successfully");

      setSelectedBill(null);
      setShowVoid(false);
      setVoidBillId(null);
      setVoidReason("");

      await searchBills();
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      setVoidLoading(false);
    }
  };

  const checkDayOpening = async () => {
    const res = await fetch("/api/pos/day-opening", {
      credentials: "include",
    });

    const data = await res.json();

    if (data.opened) {
      setShowOpening(false);
    } else {
      setShowOpening(true);
    }
  };

  const searchBills = async () => {
    const params = new URLSearchParams();

    if (billSearch) params.append("bill_no", billSearch);

    if (tokenSearch) params.append("token_no", tokenSearch);

    const res = await fetch(
      `/api/pos/bills?${params.toString()}`,
      {
        credentials: "include",
      },
    );

    const data = await res.json();

    if (data.status === "success") setBillResults(data.data);
  };

  const loadBill = async (id) => {
    const res = await fetch(`/api/pos/bill/${id}`, {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") setSelectedBill(data.bill);
    console.log(data.bill);
  };

  const openDay = async () => {
    const res = await fetch("/api/pos/day-opening", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        opening_cash: Number(openingCash),
        remarks: openingRemarks,
      }),
    });

    const data = await res.json();

    await loadHolds();
    if (data.status === "success") {
      setShowOpening(false);
    } else {
      alert(data.message);
    }
  };

  const closeDay = async () => {
    setClosingLoading(true);

    try {
      const res = await fetch("/api/pos/day-closing", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          actual_cash: Number(closingActualCash),
          expenses: Number(closingExpenses),
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      alert("Day Closed Successfully");

      window.location.href = "/billinglogin";
    } catch (err) {
      console.error(err);
    } finally {
      setClosingLoading(false);
    }
  };

  useEffect(() => {
    setBillNo(refundMode ? `RF-${genBillNo()}` : genBillNo());
    setInvoiceId(genInvoiceId());
  }, [refundMode]);

  const loadMenu = async () => {
    try {
      const res = await fetch("/api/pos/menu", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setCategories(data.data);

        if (!activeCat && data.data.length > 0) {
          setActiveCat(data.data[0].id);
        }
      }
    } catch (err) {
      console.error("MENU ERROR:", err);
    }
  };

  const saveBill = async () => {
    const res = await fetch("/api/pos/bill", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: makePayloadItems(bill),
        discount,
        payment_mode: mode,
        remarks,
        refund_mode: refundMode,
        sub_total: subTotal,
        cgst,
        sgst,
        grand_total: grandTotal,
        hold_id: holdId,
      }),
    });

    return res.json();
  };

  const saveExpense = async () => {
    if (!expenseType) {
      alert("Select expense type");
      return;
    }

    if (!expenseAmount || Number(expenseAmount) <= 0) {
      alert("Enter valid amount");
      return;
    }

    setExpenseLoading(true);

    try {
      const res = await fetch("/api/pos/expense", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          expense_type: expenseType,
          amount: Number(expenseAmount),
          remarks: expenseRemarks,
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      alert("Expense Added");

      setShowExpense(false);
      setExpenseType("");
      setExpenseAmount("");
      setExpenseRemarks("");

      loadDayClosing();
    } catch (err) {
      console.error(err);
      alert("Server Error");
    } finally {
      setExpenseLoading(false);
    }
  };

  const addItem = (item) => {
    const existing = bill.find((line) => line.id === item.id);

    if (existing) {
      if (isWeightItem(item)) {
        setSelectedRow(item.id);
        setKeypadMode("weight");
        return;
      }

      setBill(
        bill.map((line) =>
          line.id === item.id ? { ...line, qty: line.qty + 1 } : line,
        ),
      );
      return;
    }

    setBill([
      ...bill,
      {
        ...item,
        uom: item.uom || "PCS",
        qty: 1,
        grams: isWeightItem(item) ? 0 : null,
        overriddenPrice: null,
      },
    ]);

    setSelectedRow(item.id);
    setKeypadMode(isWeightItem(item) ? "weight" : "qty");
  };

  const updateQty = (id, delta) => {
    setBill(
      bill.map((b) =>
        b.id === id ? { ...b, qty: Math.max(1, b.qty + delta) } : b,
      ),
    );
  };

  const removeItem = (id) => setBill(bill.filter((b) => b.id !== id));

  const subTotal = useMemo(() => {
    const raw = bill.reduce((total, item) => total + lineAmount(item), 0);
    return refundMode ? -raw : raw;
  }, [bill, refundMode]);

  const cgst = subTotal * 0.025;
  const sgst = subTotal * 0.025;
  const gross = subTotal + cgst + sgst - discount;
  const roundOff = Math.round(gross) - gross;
  const grandTotal = Math.round(gross);

  const invoiceQR = `Invoice:${billNo}|${invoiceId}|₹${grandTotal}`;
  const upiQR = `upi://pay?pa=${STORE.upi}&pn=${STORE.name}&am=${grandTotal}`;
  const change = paid - grandTotal;

  const onKeypad = (k) => {
    if (!selectedRow && keypadMode !== "paid") return;

    const applyToRow = (fn) => {
      setBill(bill.map((b) => (b.id === selectedRow ? fn(b) : b)));
    };

    if (k === "C") {
      if (keypadMode === "qty") applyToRow((b) => ({ ...b, qty: 1 }));
      if (keypadMode === "weight") applyToRow((b) => ({ ...b, grams: 0 }));
      if (keypadMode === "price")
        applyToRow((b) => ({ ...b, overriddenPrice: null }));
      if (keypadMode === "paid") setPaid(0);
      return;
    }

    if (k === "DEL") {
      if (keypadMode === "qty")
        applyToRow((b) => ({ ...b, qty: Math.floor(b.qty / 10) || 1 }));
      if (keypadMode === "weight")
        applyToRow((b) => ({ ...b, grams: Math.floor((b.grams || 0) / 10) }));
      if (keypadMode === "price")
        applyToRow((b) => ({
          ...b,
          overriddenPrice: Math.floor((b.overriddenPrice || 0) / 10),
        }));
      if (keypadMode === "paid") setPaid(Math.floor(paid / 10));
      return;
    }

    const num = k === "." ? "." : Number(k);

    if (keypadMode === "qty") {
      applyToRow((b) => {
        const next =
          String(b.qty) === "1" ? String(num) : String(b.qty) + String(num);
        return { ...b, qty: Math.max(1, Number(next)) };
      });
    }

    if (keypadMode === "weight") {
      applyToRow((b) => {
        const next = String(b.grams || "") + String(num);
        return { ...b, grams: Number(next) };
      });
    }

    if (keypadMode === "price") {
      applyToRow((b) => {
        const next = String(b.overriddenPrice || "") + String(num);
        return { ...b, overriddenPrice: Number(next) };
      });
    }

    if (keypadMode === "paid") {
      const next =
        String(paid) === "0" ? String(num) : String(paid) + String(num);
      setPaid(Number(next));
    }
  };

  const allItems = useMemo(() => {
    return categories.flatMap((c) => c.items || []);
  }, [categories]);

  const fastItems = useMemo(() => {
    return allItems.filter((item) => item.is_fast_moving);
  }, [allItems]);

  const doPrint = () => {
    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        body * { visibility: hidden; }
        #receipt, #receipt * { visibility: visible; }
        #receipt { position: absolute; left: 0; top: 0; width: 220px; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const printOldBill = () => {
    const style = document.createElement("style");

    style.innerHTML = `
    @media print{
      body *{
        visibility:hidden;
      }

      #searchReceipt,
      #searchReceipt *{
        visibility:visible;
      }

      #searchReceipt{
        position:absolute;
        left:0;
        top:0;
        width:320px;
      }
    }
  `;

    document.head.appendChild(style);

    window.print();

    document.head.removeChild(style);
  };

  const holdBill = async () => {
    if (bill.length === 0) {
      alert("No items in bill");
      return;
    }

    const items = bill.map((item) => ({
      id: item.id,
      name: item.name,
      qty: isWeightItem(item)
        ? Number(item.grams || 0) / 1000
        : Number(item.qty || 0),
      grams: isWeightItem(item) ? Number(item.grams || 0) : null,
      price: Number(item.overriddenPrice ?? item.price),
    }));

    console.log("HOLD ITEMS BEING SENT:", items);

    const invalidWeight = items.find(
      (item) => item.grams !== null && item.grams <= 0,
    );

    if (invalidWeight) {
      alert(`Enter grams for ${invalidWeight.name}`);
      return;
    }

    try {
      const res = await fetch("/api/pos/hold", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          hold_id: holdId,
          items,
          subtotal: subTotal,
          discount,
          grand_total: grandTotal,
          remarks,
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message || "Could not hold bill");
        return;
      }

      alert("Bill held successfully");

      setBill([]);
      setDiscount(0);
      setPaid(0);
      setRemarks("");
      setRefundMode(false);
      setHoldId(null);
      setSelectedRow(null);

      await loadHolds();
    } catch (error) {
      console.error(error);
      alert("Could not hold bill");
    }
  };

  const recallBill = async (id) => {
    try {
      const res = await fetch(`/api/pos/hold/${id}`, {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message || "Could not recall bill");
        return;
      }

      const hold = data.data;

      setHoldId(hold.id);

      setBill(
        hold.items.map((item) => ({
          id: item.menu_item_id,
          name: item.item_name,
          qty: Number(item.qty || 0),
          price: Number(item.rate || 0),
          uom: item.uom || "PCS",
          grams:
            item.grams !== null && item.grams !== undefined
              ? Number(item.grams)
              : null,
          overriddenPrice: null,
        })),
      );

      setDiscount(Number(hold.discount || 0));
      setRemarks(hold.remarks || "");
      setSelectedRow(null);
    } catch (error) {
      console.error(error);
      alert("Could not recall bill");
    }
  };

  const refundBill = async () => {
    if (!refundReason.trim()) {
      alert("Enter refund reason");
      return;
    }

    setRefundLoading(true);

    try {
      const res = await fetch("/api/pos/refund-bill", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bill_id: refundBillId,
          reason: refundReason,
          return_to_inventory: returnToInventory,
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      alert("Refund completed");

      setShowRefund(false);
      setRefundReason("");
      setReturnToInventory(true);
      setSelectedBill(null);

      searchBills();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setRefundLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (barcodeTimer.current) clearTimeout(barcodeTimer.current);
      if (e.key === "Enter") {
        const code = barcodeBuffer.current;
        barcodeBuffer.current = "";
        const it = allItems.find((x) => x.barcode === code);
        if (it) addItem(it);
      } else {
        barcodeBuffer.current += e.key;
        barcodeTimer.current = setTimeout(() => {
          barcodeBuffer.current = "";
        }, 200);
      }
    };
    window.addEventListener("keypress", onKey);
    return () => window.removeEventListener("keypress", onKey);
  }, [allItems]);

  useEffect(() => {
    const onShort = (e) => {
      if (e.key === "F2") setShowDiscount(true);
      if (e.key === "F4") setShowPay(true);
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        doPrint();
      }
      if (e.key === "Escape") {
        setShowDiscount(false);
        setShowPay(false);
        setShowRemarks(false);
      }
    };
    window.addEventListener("keydown", onShort);
    return () => window.removeEventListener("keydown", onShort);
  }, []);

  const selectedCategory = categories.find((c) => c.id === activeCat);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white">
      {/* TOP BRAND HEADER */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-yellow-500/20 bg-black/40 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-yellow-400 text-black flex items-center justify-center font-bold text-lg">
            JS
          </div>
          <div>
            <div className="text-yellow-400 text-lg font-semibold tracking-wide">
              Jaggi Sweets
            </div>
            <div className="text-xs text-gray-400">
              Official Billing & POS System
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6 text-sm">
          <button
            onClick={() => setShowExpense(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
          >
            Add Expense
          </button>
          <button
            onClick={() => setShowSearch(true)}
            className="rounded-lg p-3 bg-zinc-900 border border-yellow-500/30"
          >
            Bill Search
          </button>
          <div>
            <span className="text-gray-400">Branch:</span>{" "}
            <span className="text-yellow-400">{user?.branch_name || "-"}</span>
          </div>
          <div>
            <span className="text-gray-400">Counter:</span>{" "}
            <span className="text-yellow-400">{user?.counter_name || "-"}</span>
          </div>
          <div>
            <span className="text-gray-400">Operator:</span>{" "}
            <span className="text-yellow-400">{user?.name || "-"}</span>
          </div>
          <div>
            <span className="text-gray-400">Mode:</span>{" "}
            <span className="text-yellow-400">
              {refundMode ? "Refund" : "Sale"}
            </span>
          </div>
          <button
            onClick={async () => {
              await fetch("/api/billing/logout", {
                method: "POST",
                credentials: "include",
              });

              window.location.href = "/billinglogin";
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* POS GRID */}
      <div className="p-3 grid grid-cols-4 grid-rows-2 gap-3">
        <div className="row-span-1 rounded-xl bg-white/5 backdrop-blur-xl border border-yellow-500/20 p-3">
          <div className="text-yellow-400 text-xs tracking-widest mb-2">
            GROUPS
          </div>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`rounded-lg p-3 text-sm border ${
                  activeCat === c.id
                    ? "bg-yellow-400 text-black"
                    : "bg-zinc-900"
                } border-yellow-500/30`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="mt-3 text-xs text-yellow-400">Held Bills</div>
          {holds.map((h) => (
            <button
              key={h.id}
              onClick={() => recallBill(h.id)}
              className="w-full mt-1 rounded-lg p-2 bg-zinc-900 border border-yellow-500/30 flex justify-between"
            >
              <span>{h.hold_no}</span>
              <span>₹{h.grand_total}</span>
            </button>
          ))}
        </div>

        <div className="col-span-2 row-span-1 rounded-xl bg-white/5 backdrop-blur-xl border border-yellow-500/20 p-3">
          <div className="text-yellow-400 text-xs tracking-widest mb-2">
            ITEMS
          </div>
          <div className="grid grid-cols-3 gap-2">
            {selectedCategory?.items.map((i) => (
              <button
                key={i.id}
                onClick={() => addItem(i)}
                disabled={i.stock <= 0}
                className={`rounded-lg p-3 border
        ${
          i.stock <= 0
            ? "bg-gray-700 text-gray-400 cursor-not-allowed opacity-60"
            : "bg-zinc-900 border-yellow-500/30 hover:bg-yellow-500 hover:text-black"
        }`}
              >
                <div className="text-sm">{i.name}</div>
                <div className="text-xs text-yellow-400">₹{i.price}</div>
                <div className="text-xs">Stock: {i.stock}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-white/5 backdrop-blur-xl border border-yellow-500/20 p-3">
          <div className="text-yellow-400 text-xs tracking-widest mb-2">
            FAST
          </div>

          {fastItems.length === 0 ? (
            <div className="text-gray-400 text-sm">No fast moving items</div>
          ) : (
            fastItems.map((item) => (
              <button
                key={item.id}
                onClick={() => addItem(item)}
                className="w-full mb-2 rounded-lg p-3 bg-zinc-900 border border-yellow-500/30 text-left"
              >
                <div>{item.name}</div>
                <div className="text-xs text-yellow-400">₹{item.price}</div>
              </button>
            ))
          )}
        </div>

        <div className="col-span-2 rounded-xl bg-white/5 backdrop-blur-xl border border-yellow-500/20 p-3">
          <div className="text-yellow-400 text-xs tracking-widest mb-2">
            BILL
          </div>
          <div className="max-h-56 overflow-auto">
            {bill.map((b, idx) => (
              <div
                key={b.id}
                onClick={() => {
                  setSelectedRow(b.id);
                  setKeypadTarget("qty");
                }}
                className={`grid grid-cols-12 gap-1 text-sm py-1 ${
                  selectedRow === b.id ? "bg-yellow-500/10" : ""
                }`}
              >
                <div className="col-span-1 text-yellow-400">{idx + 1}</div>
                <div className="col-span-5">{b.name}</div>
                <div className="col-span-2 flex items-center gap-1">
                  {isWeightItem(b) ? (
                    <span>{Number(b.grams || 0)}g</span>
                  ) : (
                    <>
                      <button
                        onClick={() => updateQty(b.id, -1)}
                        className="px-2"
                      >
                        -
                      </button>
                      <span>
                        {b.qty} {b.uom || "PCS"}
                      </span>
                      <button
                        onClick={() => updateQty(b.id, 1)}
                        className="px-2"
                      >
                        +
                      </button>
                    </>
                  )}
                </div>

                <div className="col-span-3 text-right">
                  ₹{lineAmount(b).toFixed(2)}
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => removeItem(b.id)}>✕</button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-2 text-xs space-y-1">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>₹{subTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>CGST 2.5%</span>
              <span>₹{cgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>SGST 2.5%</span>
              <span>₹{sgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Discount</span>
              <span>-₹{discount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Round Off</span>
              <span>₹{roundOff.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-yellow-400 font-semibold text-base">
              <span>{refundMode ? "REFUND" : "NET"}</span>
              <span>
                ₹{refundMode ? `-${Math.abs(grandTotal)}` : grandTotal}
              </span>
            </div>
          </div>
        </div>

        <div className="col-span-1 bg-white text-black p-3 font-mono text-xs rounded-xl">
          <div id="receipt" className="w-[320px] mx-auto">
            {refundMode && (
              <div className="text-center font-bold text-red-600">
                *** REFUND RECEIPT ***
              </div>
            )}
            <div className="text-center font-bold">{STORE.name}</div>
            <div className="text-center">{STORE.tagline}</div>
            <div className="text-center">Bill: {billNo}</div>
            <div className="text-center">Inv: {invoiceId}</div>
            <div className="text-center">Token: {tokenNo}</div>

            <hr className="my-1" />
            <div className="text-center font-bold">Items</div>
            {bill.map((b) => (
              <div key={b.id} className="flex justify-between">
                <span>{b.name}</span>
                <span>
                  {isWeightItem(b)
                    ? `${Number(b.grams || 0)}g × ₹${b.overriddenPrice ?? b.price}/kg`
                    : `${b.qty} ${b.uom || "PCS"} × ₹${b.overriddenPrice ?? b.price}`}
                </span>
              </div>
            ))}
            <hr className="my-2" />
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>₹{subTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>CGST</span>
              <span>₹{cgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>SGST</span>
              <span>₹{sgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Discount</span>
              <span>-₹{discount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <strong>Total</strong>
              <strong>
                ₹{refundMode ? `-${Math.abs(grandTotal)}` : grandTotal}
              </strong>
            </div>
            <div className="text-center mt-1">{remarks || "Visit Again"}</div>
            {STORE.footer.map((f, i) => (
              <div key={i} className="text-center text-[10px]">
                {f}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-white/5 backdrop-blur-xl border border-yellow-500/20 p-3">
          <div className="text-yellow-400 text-xs tracking-widest mb-2">
            KEYPAD
          </div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <button
              onClick={() => {
                const selected = bill.find((item) => item.id === selectedRow);

                if (!selected) return alert("Select an item first");

                if (isWeightItem(selected)) {
                  return alert("Use WT for KG items");
                }

                setKeypadMode("qty");
              }}
              className="btn"
            >
              QTY
            </button>

            <button
              onClick={() => {
                const selected = bill.find((item) => item.id === selectedRow);

                if (!selected) return alert("Select an item first");

                if (!isWeightItem(selected)) {
                  return alert("Weight billing is available only for KG items");
                }

                setKeypadMode("weight");
              }}
              className="btn"
            >
              WT
            </button>

            <button
              onClick={() => {
                if (!selectedRow) return alert("Select an item first");
                setKeypadMode("price");
              }}
              className="btn"
            >
              PRICE
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              onClick={() => {
                if (!selectedRow) return;
                setBill(bill.filter((b) => b.id !== selectedRow));
                setSelectedRow(null);
              }}
              className="rounded-lg p-3 bg-red-600"
            >
              VOID LINE
            </button>

            <button
              onClick={() => setShowClearConfirm(true)}
              className="rounded-lg p-3 bg-red-700"
            >
              CLEAR BILL
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "DEL"].map(
              (k) => (
                <button
                  key={k}
                  onClick={() => onKeypad(k)}
                  className="rounded-lg p-3 bg-zinc-900 border border-yellow-500/30"
                >
                  {k}
                </button>
              ),
            )}
            <button
              onClick={() => onKeypad("C")}
              className="col-span-3 rounded-lg p-3 bg-zinc-800 border border-yellow-500/30"
            >
              Clear
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <button
              onClick={() => {
                setKeypadTarget("paid");
                setMode("Cash");
                setShowPay(true);
              }}
              className="rounded-lg p-3 bg-yellow-400 text-black"
            >
              Cash
            </button>
            <button
              onClick={() => {
                setKeypadTarget("paid");
                setMode("UPI");
                setShowPay(true);
              }}
              className="rounded-lg p-3 bg-yellow-400 text-black"
            >
              UPI
            </button>
            <button
              onClick={() => {
                setKeypadTarget("paid");
                setMode("Card");
                setShowPay(true);
              }}
              className="rounded-lg p-3 bg-yellow-400 text-black"
            >
              Card
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={() => setShowDiscount(true)}
              className="rounded-lg p-3 bg-zinc-900 border border-yellow-500/30"
            >
              Discount
            </button>
            <button
              onClick={doPrint}
              className="rounded-lg p-3 bg-zinc-900 border border-yellow-500/30"
            >
              Print
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={holdBill}
              className="rounded-lg p-3 bg-zinc-900 border border-yellow-500/30"
            >
              Hold
            </button>
            <button
              onClick={() => setRefundMode(!refundMode)}
              className={`rounded-lg p-3 ${
                refundMode ? "bg-red-500" : "bg-zinc-900"
              } border border-yellow-500/30`}
            >
              {refundMode ? "REFUND MODE" : "SALE MODE"}
            </button>
          </div>
          <button
            onClick={() => setShowRemarks(true)}
            className="mt-2 w-full rounded-lg p-3 bg-zinc-900 border border-yellow-500/30"
          >
            Remarks
          </button>
          <button
            onClick={loadDayClosing}
            className="mt-2 w-full rounded-lg p-3 bg-red-600 text-white"
          >
            Day Closing
          </button>
        </div>

        {showDiscount && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center">
            <div className="bg-zinc-900 p-5 rounded-xl w-80 border border-yellow-500/30">
              <div className="text-yellow-400 mb-2">Apply Discount</div>
              <input
                className="w-full bg-black p-2 rounded"
                type="number"
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value))}
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => setShowDiscount(false)}
                  className="px-3 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowDiscount(false)}
                  className="px-3 py-2 bg-yellow-400 text-black rounded"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {showPay && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center">
            <div className="bg-zinc-900 p-5 rounded-xl w-80 border border-yellow-500/30">
              <div className="text-yellow-400 mb-2">Payment</div>
              <select
                className="w-full bg-black p-2 rounded"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                <option>Cash</option>
                <option>UPI</option>
                <option>Card</option>
              </select>
              <input
                className="w-full bg-black p-2 rounded mt-2"
                placeholder="Paid Amount"
                type="number"
                value={paid}
                onChange={(e) => setPaid(Number(e.target.value))}
              />
              <div className="mt-2 text-sm">
                {refundMode ? "Return:" : "Change:"} ₹{Math.abs(change)}
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setShowPay(false)} className="px-3 py-2">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setSaving(true);
                    try {
                      if (bill.length === 0) {
                        alert("No items in bill");
                        return;
                      }

                      if (!refundMode && mode === "Cash" && paid < grandTotal) {
                        alert("Insufficient cash received");
                        return;
                      }

                      const invalidWeight = bill.find(
                        (item) =>
                          isWeightItem(item) && Number(item.grams || 0) <= 0,
                      );

                      if (invalidWeight) {
                        alert(`Enter grams for ${invalidWeight.name}`);
                        return;
                      }

                      const data = await saveBill();

                      setBillNo(data.bill_no);
                      setTokenNo(data.token_no);

                      setLastPrintedBill({
                        bill_no: data.bill_no,
                        token_no: data.token_no,
                        items: bill,
                        total: grandTotal,
                      });

                      if (data.status !== "success") {
                        alert(data.message || "Failed to save bill");
                        return;
                      }

                      alert(
                        refundMode ? "Refund Completed" : "Payment Successful",
                      );

                      setBill([]);
                      setPaid(0);
                      setDiscount(0);
                      setRemarks("");
                      setRefundMode(false);
                      setHoldId(null);

                      await loadMenu();

                      setBillNo(genBillNo());
                      setInvoiceId(genInvoiceId());

                      await loadHolds();
                      setShowPay(false);
                    } catch (err) {
                      console.error(err);
                      alert("Server error");
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="px-3 py-2 bg-yellow-400 text-black rounded"
                  disabled={saving || bill.length === 0}
                >
                  {saving ? "Saving..." : "Complete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRemarks && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center">
            <div className="bg-zinc-900 p-5 rounded-xl w-80 border border-yellow-500/30">
              <div className="text-yellow-400 mb-2">Remarks</div>
              <textarea
                className="w-full bg-black p-2 rounded"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add a note on receipt..."
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => setShowRemarks(false)}
                  className="px-3 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowRemarks(false)}
                  className="px-3 py-2 bg-yellow-400 text-black rounded"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {showClearConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center">
            <div className="bg-zinc-900 p-5 rounded-xl w-80 border border-yellow-500/30">
              <div className="text-yellow-400 mb-2">Clear Bill?</div>
              <p className="text-sm text-gray-400">
                This will remove all items.
              </p>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setShowClearConfirm(false)}>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setBill([]);
                    setShowClearConfirm(false);
                  }}
                  className="bg-red-600 px-3 py-2 rounded"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showOpening && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center">
          <div className="bg-zinc-900 p-6 rounded-xl w-96">
            <h2 className="text-xl text-yellow-400 mb-4">Day Opening</h2>

            <input
              type="number"
              placeholder="Opening Cash"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              className="w-full bg-black p-3 rounded"
            />

            <textarea
              placeholder="Remarks"
              value={openingRemarks}
              onChange={(e) => setOpeningRemarks(e.target.value)}
              className="w-full bg-black p-3 rounded mt-3"
            />

            <button
              onClick={openDay}
              className="w-full mt-4 bg-yellow-400 text-black p-3 rounded"
            >
              Start Billing
            </button>
          </div>
        </div>
      )}

      {showDayClosing && closingSummary && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 w-[450px] rounded-xl p-6 border border-yellow-500">
            <h2 className="text-xl font-bold text-yellow-400 mb-5">
              Day Closing
            </h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span>Opening Cash</span>
                <span>₹{closingSummary.opening_cash.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span>Cash Sales</span>
                <span className="text-green-400">
                  ₹{closingSummary.cash_sales.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between">
                <span>UPI Sales</span>
                <span>₹{closingSummary.upi_sales.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span>Card Sales</span>
                <span>₹{closingSummary.card_sales.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span>Refunds</span>
                <span className="text-red-400">
                  ₹{closingSummary.refunds.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between">
                <span>Expenses</span>
                <span className="text-red-400">
                  ₹{closingSummary.expenses.toFixed(2)}
                </span>
              </div>
            </div>

            <hr className="my-5 border-zinc-700" />

            <div className="flex justify-between font-bold text-yellow-400 text-lg mb-5">
              <span>Expected Cash</span>
              <span>₹{closingSummary.expected_cash.toFixed(2)}</span>
            </div>

            <label className="block text-sm mb-2">Actual Cash Counted</label>

            <input
              type="number"
              value={closingActualCash}
              onChange={(e) => setClosingActualCash(e.target.value)}
              className="w-full p-3 rounded bg-black border border-gray-600 text-white"
            />

            <div className="flex justify-between mt-5 text-base">
              <span>Difference</span>

              <span
                className={
                  Number(closingActualCash) === closingSummary.expected_cash
                    ? "text-green-400 font-bold"
                    : Number(closingActualCash) > closingSummary.expected_cash
                      ? "text-blue-400 font-bold"
                      : "text-red-400 font-bold"
                }
              >
                ₹
                {(
                  Number(closingActualCash) - closingSummary.expected_cash
                ).toFixed(2)}
              </span>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDayClosing(false)}
                className="px-4 py-2 bg-zinc-700 rounded"
              >
                Cancel
              </button>

              <button
                onClick={closeDay}
                disabled={closingLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded"
              >
                {closingLoading ? "Closing..." : "Close Day"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVoid && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000]">
          <div className="bg-zinc-900 rounded-xl p-6 w-[400px]">
            <h2 className="text-xl font-semibold text-red-500 mb-4">
              Void Bill
            </h2>

            <textarea
              className="w-full bg-black rounded p-3 border border-zinc-700"
              rows={4}
              placeholder="Reason for void..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => {
                  setShowVoid(false);
                  setVoidReason("");
                }}
                className="bg-zinc-700 px-4 py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={voidBill}
                disabled={voidLoading}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded"
              >
                {voidLoading ? "Voiding..." : "Void Bill"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSearch && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center">
          <div className="bg-zinc-900 rounded-xl p-5 w-[900px]">
            <h2 className="text-xl mb-4">Bill Search</h2>

            <div className="flex gap-2">
              <input
                placeholder="Bill No"
                value={billSearch}
                onChange={(e) => setBillSearch(e.target.value)}
                className="bg-black p-2 rounded w-full"
              />

              <input
                placeholder="Token"
                value={tokenSearch}
                onChange={(e) => setTokenSearch(e.target.value)}
                className="bg-black p-2 rounded w-full"
              />

              <button
                onClick={searchBills}
                className="bg-yellow-500 text-black px-4 rounded"
              >
                Search
              </button>
            </div>

            <div className="mt-5 max-h-[400px] overflow-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Bill</th>
                    <th>Token</th>
                    <th>Total</th>
                    <th>Mode</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {billResults.map((b) => (
                    <tr key={b.id}>
                      <td>{b.bill_no}</td>

                      <td>{b.token_no}</td>

                      <td>₹{b.grand_total}</td>

                      <td>{b.payment_mode}</td>

                      <td>
                        {b.void ? "VOID" : b.refund ? "REFUND" : "NORMAL"}
                      </td>

                      <td>
                        <button
                          onClick={() => loadBill(b.id)}
                          className="text-yellow-400"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => setShowSearch(false)}
              className="mt-5 bg-red-500 px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {selectedBill && (
        <div className="fixed inset-0 bg-black/80 flex justify-center items-center">
          <div className="bg-zinc-900 rounded-xl p-6 w-[500px]">
            <h2>{selectedBill.bill_no}</h2>

            <h3>Token : {selectedBill.token_no}</h3>

            <hr className="my-3" />

            {selectedBill.items.map((i, index) => (
              <div key={index} className="flex justify-between">
                <span>
                  {i.name}({i.qty})
                </span>

                <span>₹{i.amount}</span>
              </div>
            ))}

            <hr className="my-3" />

            <div className="font-bold">Total ₹{selectedBill.grand_total}</div>
            <div className="font-bold">
              Payment : {selectedBill.payment_mode}
            </div>
            <div className="font-bold">
              Remarks : {selectedBill.remarks || "-"}
            </div>

            <div className="hidden">
              <BillReceipt bill={selectedBill} />
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={printOldBill}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
              >
                Reprint
              </button>

              <button
                onClick={() => {
                  if (selectedBill.void) return;

                  setVoidBillId(selectedBill.id);
                  setShowVoid(true);
                }}
                disabled={selectedBill?.void === true}
                className={`px-4 py-2 rounded ${
                  selectedBill?.void
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {selectedBill?.void ? "Voided" : "Void"}
              </button>

              <button
                onClick={() => {
                  setRefundBillId(selectedBill.id);
                  setShowRefund(true);
                }}
                disabled={selectedBill.refund || selectedBill.void}
                className={`px-4 py-2 rounded ${
                  selectedBill.refund || selectedBill.void
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-yellow-500 hover:bg-yellow-600 text-black"
                }`}
              >
                {selectedBill.refund ? "Refunded" : "Refund"}
              </button>
              <button
                onClick={() => setSelectedBill(null)}
                className="mt-5 bg-zinc-700 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showRefund && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 p-6 rounded-xl w-[420px]">
            <h2 className="text-xl text-yellow-400 mb-4">Refund Bill</h2>

            <textarea
              rows={4}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Reason for refund..."
              className="w-full bg-black border border-zinc-700 rounded p-3"
            />

            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={returnToInventory}
                  onChange={(e) => setReturnToInventory(e.target.checked)}
                  className="w-4 h-4"
                />

                <span>Return items to inventory</span>
              </label>

              <p className="text-xs text-gray-400 mt-2">
                Uncheck this if the returned items are damaged, spoiled, or
                cannot be sold again.
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowRefund(false);
                  setRefundReason("");
                  setReturnToInventory(true);
                }}
                className="bg-zinc-700 px-4 py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={refundBill}
                disabled={refundLoading}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded"
              >
                {refundLoading ? "Refunding..." : "Refund"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExpense && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 rounded-xl w-[450px] border border-yellow-500/20 p-6">
            <h2 className="text-2xl font-semibold text-yellow-400 mb-6">
              Add Expense
            </h2>

            {/* Expense Type */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-300 mb-2">
                Expense Type
              </label>

              <select
                value={expenseType}
                onChange={(e) => setExpenseType(e.target.value)}
                className="w-full bg-black border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="">Select Expense</option>
                <option value="Petty Cash">Petty Cash</option>
                <option value="Cleaning">Cleaning</option>
                <option value="Packaging">Packaging</option>
                <option value="Transportation">Transportation</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Staff Meal">Staff Meal</option>
                <option value="Fuel">Fuel</option>
                <option value="Electricity">Electricity</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-300 mb-2">Amount</label>

              <input
                type="number"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full bg-black border border-zinc-700 rounded-lg p-3 text-white"
              />
            </div>

            {/* Remarks */}
            <div className="mb-6">
              <label className="block text-sm text-zinc-300 mb-2">
                Remarks
              </label>

              <textarea
                rows={4}
                value={expenseRemarks}
                onChange={(e) => setExpenseRemarks(e.target.value)}
                placeholder="Remarks (optional)"
                className="w-full bg-black border border-zinc-700 rounded-lg p-3 text-white"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowExpense(false);
                  setExpenseType("");
                  setExpenseAmount("");
                  setExpenseRemarks("");
                }}
                className="bg-zinc-700 hover:bg-zinc-600 px-5 py-2 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={saveExpense}
                disabled={expenseLoading}
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-5 py-2 rounded-lg font-semibold"
              >
                {expenseLoading ? "Saving..." : "Save Expense"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
